import React, { useId } from 'react';
import type { RewardTier } from '../../../shared/types';
import { LightbulbIcon, WarningIcon } from '../icons/UiIcons';

interface PointsConfigState {
    generosity: number;        // [1, 10] pts/€
    rewardLabel: string;       // récompense principale
    targetPoints: number;      // seuil de la récompense principale
    rewardTiers: RewardTier[]; // paliers additionnels (max 3)
}

interface PointsConfigProps {
    value: PointsConfigState;
    onChange: (next: PointsConfigState) => void;
    errors?: string[];
    disabled?: boolean;
}

const SAMPLE_BILL_EUROS = 25;
const MAX_ADDITIONAL_TIERS = 3;

const PointsConfig: React.FC<PointsConfigProps> = ({ value, onChange, errors, disabled }) => {
    const uid = useId();
    const sampleEarn = SAMPLE_BILL_EUROS * value.generosity;
    const mealsToReward = value.targetPoints > 0 ? Math.ceil(value.targetPoints / sampleEarn) : 0;

    const setGenerosity = (v: number) => onChange({ ...value, generosity: Math.max(1, Math.min(10, v)) });
    const setRewardLabel = (label: string) => onChange({ ...value, rewardLabel: label });
    const setTargetPoints = (n: number) => onChange({ ...value, targetPoints: Math.max(1, n) });

    const addTier = () => {
        if (value.rewardTiers.length >= MAX_ADDITIONAL_TIERS) return;
        const lastThreshold = value.rewardTiers.length > 0
            ? value.rewardTiers[value.rewardTiers.length - 1].threshold
            : value.targetPoints;
        onChange({
            ...value,
            rewardTiers: [...value.rewardTiers, { label: '', threshold: lastThreshold + 50 }],
        });
    };

    const updateTier = (idx: number, patch: Partial<RewardTier>) => {
        onChange({
            ...value,
            rewardTiers: value.rewardTiers.map((t, i) => i === idx ? { ...t, ...patch } : t),
        });
    };

    const removeTier = (idx: number) => {
        onChange({
            ...value,
            rewardTiers: value.rewardTiers.filter((_, i) => i !== idx),
        });
    };

    return (
        <div className="space-y-6">
            {/* Générosité */}
            <div>
                <label id={`${uid}-gen-label`} htmlFor={`${uid}-gen`} className="block text-sm font-bold text-gray-700 mb-2">
                    Générosité <span className="text-gray-500 font-normal">— points par euro dépensé</span>
                </label>
                <div className="flex items-center gap-4">
                    <input
                        id={`${uid}-gen`}
                        type="range"
                        min={1}
                        max={10}
                        step={1}
                        value={value.generosity}
                        onChange={e => setGenerosity(parseInt(e.target.value))}
                        disabled={disabled}
                        className="flex-1 accent-brand-primary"
                    />
                    <input
                        type="number"
                        aria-labelledby={`${uid}-gen-label`}
                        min={1}
                        max={10}
                        value={value.generosity}
                        onChange={e => setGenerosity(parseInt(e.target.value) || 1)}
                        disabled={disabled}
                        className="w-20 p-2 text-sm rounded-xl border border-gray-400 bg-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    />
                    <span className="text-sm text-gray-500 whitespace-nowrap">pts/€</span>
                </div>
                <p className="mt-2 text-xs text-brand-primary bg-brand-primary/10 dark:bg-brand-primary/20 px-3 py-2 rounded-lg inline-flex items-center gap-2">
                    <LightbulbIcon className="w-4 h-4 flex-shrink-0" />
                    <span>Un repas à {SAMPLE_BILL_EUROS}€ = <strong>{sampleEarn} points</strong></span>
                </p>
            </div>

            {/* Récompense principale */}
            <div className="space-y-3">
                <p className="block text-sm font-bold text-gray-700">
                    Récompense principale
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                        <input
                            type="text"
                            placeholder="Ex: café offert, dessert offert, réduction 10€"
                            aria-label="Nom de la récompense principale"
                            value={value.rewardLabel}
                            onChange={e => setRewardLabel(e.target.value)}
                            disabled={disabled}
                            className="w-full p-2.5 rounded-xl border border-gray-400 bg-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                        />
                    </div>
                    <div className="flex min-w-0 items-center gap-2">
                        <input
                            type="number"
                            min={1}
                            aria-label="Points nécessaires pour la récompense principale"
                            value={value.targetPoints}
                            onChange={e => setTargetPoints(parseInt(e.target.value) || 1)}
                            disabled={disabled}
                            className="min-w-0 flex-1 p-2.5 rounded-xl border border-gray-400 bg-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                        />
                        <span className="text-sm text-gray-500">pts</span>
                    </div>
                </div>
                {mealsToReward > 0 && value.rewardLabel && (
                    <p className="text-xs text-gray-500">
                        Soit environ <strong>{mealsToReward} repas</strong> à {SAMPLE_BILL_EUROS}€ pour obtenir « {value.rewardLabel} »
                    </p>
                )}
            </div>

            {/* Paliers additionnels */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-bold text-gray-700">
                        Paliers additionnels <span className="text-gray-500 font-normal">— optionnel ({value.rewardTiers.length}/{MAX_ADDITIONAL_TIERS})</span>
                    </p>
                </div>
                {value.rewardTiers.length === 0 && (
                    <p className="text-xs text-gray-500 italic mb-2">
                        Ajoutez jusqu'à 3 paliers supplémentaires (ex: 100 pts = dessert, 200 pts = repas).
                    </p>
                )}
                <ul className="space-y-2">
                    {value.rewardTiers.map((tier, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                            <span aria-hidden="true" className="text-xs text-gray-500 font-bold w-6">#{idx + 1}</span>
                            <input
                                type="text"
                                placeholder="Nom de la récompense"
                                aria-label={`Palier ${idx + 1} : nom de la récompense`}
                                value={tier.label}
                                onChange={e => updateTier(idx, { label: e.target.value })}
                                disabled={disabled}
                                className="flex-1 p-2 text-sm rounded-xl border border-gray-400 bg-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                            />
                            <input
                                type="number"
                                min={1}
                                aria-label={`Palier ${idx + 1} : points nécessaires`}
                                value={tier.threshold}
                                onChange={e => updateTier(idx, { threshold: parseInt(e.target.value) || 1 })}
                                disabled={disabled}
                                className="w-24 p-2 text-sm rounded-xl border border-gray-400 bg-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                            />
                            <span className="text-xs text-gray-500 whitespace-nowrap">pts</span>
                            <button
                                type="button"
                                onClick={() => removeTier(idx)}
                                disabled={disabled}
                                aria-label={`Supprimer palier ${idx + 1}`}
                                className="text-brand-600 hover:text-brand-800 disabled:opacity-40 px-2"
                            >
                                ×
                            </button>
                        </li>
                    ))}
                </ul>
                <button
                    type="button"
                    onClick={addTier}
                    disabled={disabled || value.rewardTiers.length >= MAX_ADDITIONAL_TIERS}
                    className="mt-2 px-3 py-1.5 text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg"
                >
                    + Ajouter un palier
                </button>
            </div>

            {errors && errors.length > 0 && (
                <div role="alert" className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg space-y-1">
                    {errors.map((err, i) => (
                        <p key={i} className="text-xs text-red-700 dark:text-red-300 inline-flex items-center gap-1.5">
                            <WarningIcon className="w-3.5 h-3.5 flex-shrink-0" /> {err}
                        </p>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PointsConfig;
export type { PointsConfigState };
